import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import i18n from "../../config/i18n";
import { toDictionaryAddError } from "../../features/dictionary/utils/addWordError";
import { sharedApi, getAuthToken } from "../../services/sharedApi";
import { toApiError } from "../../utils/errorUtils";

import type { ApiError } from "../../types/api";
import type { PayloadAction } from "@reduxjs/toolkit";

// Типы данных
export interface DictionaryWord {
  id: string;
  originalWord: string;
  baseWord: string;
  translation: string;
  context: string;
  sourceLanguage: string;
  targetLanguage: string;
  level: "NEW" | "LEARNING" | "FAMILIAR" | "LEARNED" | "MASTERED";
  correctAnswers: number;
  totalAttempts: number;
  lastReview: string | null;
  nextReview: string | null;
  intervalDays: number;
  createdAt: string;
  updatedAt: string;
}

export interface DictionaryStats {
  totalWords: number;
  newWords: number;
  learningWords: number;
  familiarWords: number;
  learnedWords: number;
  masteredWords: number;
  wordsForToday: number;
}

export interface StudyResult {
  wordId: string;
  isCorrect: boolean;
  timeSpent: number;
  sessionType:
    | "word_to_translation"
    | "translation_to_word"
    | "context_to_word";
}

export interface AddWordRequest {
  dictationSessionId?: string;
  textId: string;
  originalWord: string;
  targetLanguage: string;
  wordPosition: number;
}

// Состояние slice
type LevelFilter = DictionaryWord["level"] | null;
type FilterKey = keyof DictionaryState["filters"]; // forward declaration via indexed usage below (ts will reorder after patch)
type FilterValue = LevelFilter | string | number | null;

interface DictionaryState {
  words: DictionaryWord[];
  wordsRequestId: string | null;
  statsRequestId: string | null;
  studyWords: DictionaryWord[];
  stats: DictionaryStats | null;
  currentStudyWord: DictionaryWord | null;
  currentStudyIndex: number;
  studySession: {
    isActive: boolean;
    startTime: number | null;
    correctAnswers: number;
    totalAnswers: number;
  };
  filters: {
    level: LevelFilter;
    search: string;
    limit: number;
    offset: number;
  };
  loading: {
    words: boolean;
    studyWords: boolean;
    stats: boolean;
    addWord: boolean;
    studyResult: boolean;
  };
  // Флаги первой успешной загрузки для гейтинга пустых состояний
  loadedOnce: {
    words: boolean;
    studyWords: boolean;
    stats: boolean;
  };
  error: string | null;
}

const initialState: DictionaryState = {
  words: [],
  wordsRequestId: null,
  statsRequestId: null,
  studyWords: [],
  stats: null,
  currentStudyWord: null,
  currentStudyIndex: 0,
  studySession: {
    isActive: false,
    startTime: null,
    correctAnswers: 0,
    totalAnswers: 0,
  },
  filters: {
    level: null,
    search: "",
    limit: 50,
    offset: 0,
  },
  loading: {
    words: false,
    studyWords: false,
    stats: false,
    addWord: false,
    studyResult: false,
  },
  loadedOnce: {
    words: false,
    studyWords: false,
    stats: false,
  },
  error: null,
};

// API Response Types
interface ApiDictionaryWord {
  id: string;
  originalWord: string;
  baseWord?: string;
  translation: string;
  context?: string;
  sourceLanguage?: string;
  targetLanguage?: string;
  level?: number;
  correctAnswers?: number;
  totalAttempts?: number;
  lastReview?: string | null;
  learnedAt?: string | null;
  nextReview?: string | null;
  intervalDays?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface ApiDictionaryStats {
  totalWords?: number;
  newWords?: number;
  learningWords?: number;
  familiarWords?: number;
  learnedWords?: number;
  masteredWords?: number;
  wordsForToday?: number;
}

// Mapping helpers to adapt API responses to internal richer model.
function mapApiWord(api: ApiDictionaryWord): DictionaryWord {
  return {
    id: api.id,
    originalWord: api.originalWord,
    baseWord: api.baseWord || api.originalWord,
    translation: api.translation,
    context: api.context || "",
    sourceLanguage: api.sourceLanguage || "en",
    targetLanguage: api.targetLanguage || "en",
    level: (api.level || "NEW") as DictionaryWord["level"],
    correctAnswers: api.correctAnswers ?? 0,
    totalAttempts: api.totalAttempts ?? 0,
    lastReview: api.lastReview ?? null,
    nextReview: api.nextReview ?? null,
    intervalDays: api.intervalDays ?? 0,
    createdAt: api.createdAt || new Date().toISOString(),
    updatedAt: api.updatedAt || new Date().toISOString(),
  };
}

function mapApiStats(api: ApiDictionaryStats): DictionaryStats {
  return {
    totalWords: api.totalWords ?? 0,
    newWords: api.newWords ?? 0,
    learningWords: api.learningWords ?? 0,
    familiarWords: api.familiarWords ?? 0,
    learnedWords: api.learnedWords ?? 0,
    masteredWords: api.masteredWords ?? 0,
    wordsForToday: api.wordsForToday ?? 0,
  };
}

// Async thunks
export const addWordToDictionary = createAsyncThunk<
  Awaited<ReturnType<typeof sharedApi.dictionary.addWord>>["data"],
  AddWordRequest,
  { rejectValue: ApiError }
>(
  "dictionary/addWord",
  async (wordData: AddWordRequest, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const resp = await sharedApi.dictionary.addWord(wordData, token);
      return resp.data;
    } catch (error: unknown) {
      return rejectWithValue(toDictionaryAddError(error));
    }
  },
);

export const fetchUserDictionary = createAsyncThunk(
  "dictionary/fetchUserDictionary",
  async (
    params: {
      level?: string;
      search?: string;
      limit?: number;
      offset?: number;
    } = {},
    { rejectWithValue },
  ) => {
    try {
      const token = getAuthToken();
      const query: Record<string, string | number> = {};
      if (params.level) query.level = params.level;
      if (params.search) query.search = params.search;
      if (params.limit) query.limit = params.limit;
      if (params.offset) query.offset = params.offset;
      const resp = await sharedApi.dictionary.list(query, token);
      return resp.data;
    } catch (e: unknown) {
      return rejectWithValue(
        toApiError(e).message ||
          i18n.t("dictionary:errors.failedToFetchDictionary"),
      );
    }
  },
);

export const fetchWordsForStudy = createAsyncThunk(
  "dictionary/fetchWordsForStudy",
  async (limit: number = 20, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const resp = await sharedApi.dictionary.study(limit, token);
      return resp.data;
    } catch (e: unknown) {
      return rejectWithValue(
        toApiError(e).message ||
          i18n.t("dictionary:errors.failedToFetchStudyWords"),
      );
    }
  },
);

export const submitStudyResult = createAsyncThunk(
  "dictionary/submitStudyResult",
  async (studyData: StudyResult, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const resp = await sharedApi.dictionary.submitStudy(studyData, token);
      return resp.data;
    } catch (e: unknown) {
      return rejectWithValue(
        toApiError(e).message ||
          i18n.t("dictionary:errors.failedToSubmitStudyResult"),
      );
    }
  },
);

export const fetchDictionaryStats = createAsyncThunk(
  "dictionary/fetchStats",
  async (sourceLanguage: string | undefined, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      const resp = await sharedApi.dictionary.stats(sourceLanguage, token);
      return resp.data;
    } catch (e: unknown) {
      return rejectWithValue(
        toApiError(e).message || i18n.t("dictionary:errors.failedToFetchStats"),
      );
    }
  },
);

export const removeWordFromDictionary = createAsyncThunk(
  "dictionary/removeWord",
  async (wordId: string, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      await sharedApi.dictionary.remove(wordId, token);
      return wordId;
    } catch (e: unknown) {
      return rejectWithValue(
        toApiError(e).message || i18n.t("dictionary:errors.failedToRemoveWord"),
      );
    }
  },
);

// Slice
const dictionarySlice = createSlice({
  name: "dictionary",
  initialState,
  reducers: {
    // Управление сессией изучения
    startStudySession: (state) => {
      state.studySession.isActive = true;
      state.studySession.startTime = Date.now();
      state.studySession.correctAnswers = 0;
      state.studySession.totalAnswers = 0;
      state.currentStudyIndex = 0;
      state.currentStudyWord = state.studyWords[0] || null;
    },

    endStudySession: (state) => {
      state.studySession.isActive = false;
      state.studySession.startTime = null;
      state.currentStudyWord = null;
      state.currentStudyIndex = 0;
    },

    nextStudyWord: (state) => {
      if (state.currentStudyIndex < state.studyWords.length - 1) {
        state.currentStudyIndex += 1;
        state.currentStudyWord = state.studyWords[state.currentStudyIndex];
      } else {
        // Сессия завершена
        state.studySession.isActive = false;
        state.currentStudyWord = null;
      }
    },

    recordAnswer: (state, action: PayloadAction<boolean>) => {
      state.studySession.totalAnswers += 1;
      if (action.payload) {
        state.studySession.correctAnswers += 1;
      }
    },

    // Управление фильтрами
    setFilter: (
      state,
      action: PayloadAction<{ key: FilterKey; value: FilterValue }>,
    ) => {
      const { key, value } = action.payload;
      switch (key) {
        case "level": {
          state.filters.level = value as LevelFilter;
          break;
        }
        case "search": {
          if (typeof value === "string") state.filters.search = value;
          break;
        }
        case "limit": {
          if (typeof value === "number") state.filters.limit = value;
          break;
        }
        case "offset": {
          if (typeof value === "number") state.filters.offset = value;
          break;
        }
        default: {
          const _exhaustive: never = key;
          return _exhaustive;
        }
      }
    },

    resetFilters: (state) => {
      state.filters = initialState.filters;
    },

    // Очистка ошибок
    clearError: (state) => {
      state.error = null;
    },

    resetDictionaryState: () => initialState,
  },
  extraReducers: (builder) => {
    // Add word to dictionary
    builder
      .addCase(addWordToDictionary.pending, (state) => {
        state.loading.addWord = true;
        state.error = null;
      })
      .addCase(addWordToDictionary.fulfilled, (state, action) => {
        state.loading.addWord = false;
        const word = mapApiWord(action.payload);
        const index = state.words.findIndex(
          (existing) => existing.id === word.id,
        );
        if (index === -1) state.words.unshift(word);
        else state.words[index] = word;
      })
      .addCase(addWordToDictionary.rejected, (state, action) => {
        state.loading.addWord = false;
        state.error = action.payload?.message || action.error.message || null;
      });

    // Fetch user dictionary
    builder
      .addCase(fetchUserDictionary.pending, (state, action) => {
        state.wordsRequestId = action.meta.requestId;
        state.loading.words = true;
        state.error = null;
      })
      .addCase(fetchUserDictionary.fulfilled, (state, action) => {
        if (state.wordsRequestId !== action.meta.requestId) return;
        state.wordsRequestId = null;
        state.loading.words = false;
        state.words = action.payload.map(mapApiWord);
        state.loadedOnce.words = true;
      })
      .addCase(fetchUserDictionary.rejected, (state, action) => {
        if (state.wordsRequestId !== action.meta.requestId) return;
        state.wordsRequestId = null;
        state.loading.words = false;
        state.error = action.payload as string;
        // Считаем попытку загрузки состоявшейся, чтобы скрыть ложное пустое состояние
        state.loadedOnce.words = true;
      });

    // Fetch words for study
    builder
      .addCase(fetchWordsForStudy.pending, (state) => {
        state.loading.studyWords = true;
        state.error = null;
      })
      .addCase(fetchWordsForStudy.fulfilled, (state, action) => {
        state.loading.studyWords = false;
        state.studyWords = action.payload.map(mapApiWord);
        state.loadedOnce.studyWords = true;
      })
      .addCase(fetchWordsForStudy.rejected, (state, action) => {
        state.loading.studyWords = false;
        state.error = action.payload as string;
        state.loadedOnce.studyWords = true;
      });

    // Submit study result
    builder
      .addCase(submitStudyResult.pending, (state) => {
        state.loading.studyResult = true;
        state.error = null;
      })
      .addCase(submitStudyResult.fulfilled, (state, action) => {
        state.loading.studyResult = false;
        const mapped = mapApiWord(action.payload);
        const index = state.studyWords.findIndex(
          (word) => word.id === mapped.id,
        );
        if (index !== -1) state.studyWords[index] = mapped;
      })
      .addCase(submitStudyResult.rejected, (state, action) => {
        state.loading.studyResult = false;
        state.error = action.payload as string;
      });

    // Fetch dictionary stats
    builder
      .addCase(fetchDictionaryStats.pending, (state, action) => {
        state.statsRequestId = action.meta.requestId;
        state.loading.stats = true;
        state.error = null;
      })
      .addCase(fetchDictionaryStats.fulfilled, (state, action) => {
        if (state.statsRequestId !== action.meta.requestId) return;
        state.statsRequestId = null;
        state.loading.stats = false;
        state.stats = mapApiStats(action.payload);
        state.loadedOnce.stats = true;
      })
      .addCase(fetchDictionaryStats.rejected, (state, action) => {
        if (state.statsRequestId !== action.meta.requestId) return;
        state.statsRequestId = null;
        state.loading.stats = false;
        state.error = action.payload as string;
        state.loadedOnce.stats = true;
      });

    // Remove word from dictionary
    builder.addCase(removeWordFromDictionary.fulfilled, (state, action) => {
      state.words = state.words.filter((word) => word.id !== action.payload);
      state.studyWords = state.studyWords.filter(
        (word) => word.id !== action.payload,
      );
    });
  },
});

export const {
  startStudySession,
  endStudySession,
  nextStudyWord,
  recordAnswer,
  setFilter,
  resetFilters,
  clearError,
  resetDictionaryState,
} = dictionarySlice.actions;

export default dictionarySlice.reducer;
